import { select } from '@angular-redux/store';
import { Injectable, Injector } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  getFormFieldValue,
  setFormFieldValue,
} from '@app/components/form/services/schema-form/schema-form-functions';
import {
  defaultWidgetData,
  gridSelectModalOptions,
} from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.settings';
import { lazyInject } from '@app/containers/base/decorators';
import { getSiteSelectorMaxLimitDefine } from '@app/shared/modal-definitions';
import {
  BehaviorSubject,
  Observable,
  observableCombineLatest,
  rx,
  Subject,
} from '@app/shared/rx';
import {
  ITransferGroupTemplateFormFieldPath,
  ITransferGroupTemplateSite,
} from '@app/shared/types/entities/transfer-group-template/transfer-grouo-template.type';
import {
  getCreateAutoPopulateData,
  getSiteSelectorButtonCaption,
  getSiteSelectorColumnsDefinition,
} from '@app/shared/utils';
import { UiActions } from '@app/store/actions';
import {
  BTN_CANCEL,
  BTN_OK,
  BUTTON_ACTIONS,
} from '@app/text-content/button-templates';
import { MAX_PAGE_SIZE } from '@config/global-constants';
import {
  BaseFormService,
  DataSource,
  PermissionService,
  SitesService,
  STATE_KEYS,
  TransferGroupTemplateService,
  ValidationService,
} from '@shared/services';
import {
  CreateFormRules,
  EntityId,
  ENTITY_TYPES,
  IApiListResponse,
  IBaseEntityType,
  IBaseFormService,
  IFilterQuery,
  IModalDialogOptions,
  IPageJumpRouteState,
  ISite,
  IWidgetAction,
} from '@shared/types';
import {
  chain,
  concat,
  filter,
  find,
  get,
  head,
  includes,
  isEmpty,
  isEqual,
  isNil,
  map,
  merge,
  size,
} from 'lodash';
import { first } from 'rxjs/operators';
import { MAX_SITES_LIMIT } from '../transfer-group/transfer-group.constants';
import {
  addStoreNumberForSites,
  filterSelectedSites,
  isSiteTypePrimaryWarehouse,
  setPrimaryAndWarehouseTop,
} from '../transfer/transfer.util';
import { createFormRules as createTransferGroupTemplateFormRules } from './form-rules';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class TransferGroupTemplateFormService
  extends BaseFormService
  implements IBaseFormService<ENTITY_TYPES.TRANSFER_GROUP_TEMPLATE>
{
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(ValidationService) public validationService: ValidationService;
  @lazyInject(TransferGroupTemplateService)
  public transferGroupTemplateService: TransferGroupTemplateService;
  @lazyInject(PermissionService) public permissionService: PermissionService;

  @select(STATE_KEYS.UI_EDITOR_OPTIONS_STATE)
  public routeState$: Observable<IPageJumpRouteState>;

  public static ENTITY_TYPE: ENTITY_TYPES =
    ENTITY_TYPES.TRANSFER_GROUP_TEMPLATE;

  public createFormRulesFunction: CreateFormRules<TransferGroupTemplateFormService> =
    createTransferGroupTemplateFormRules;

  public formFieldPath: ITransferGroupTemplateFormFieldPath = {
    name: 'name',
    transferType: 'transfer_type',
    transferGroupName: 'transfer_group_name',
    siteId: 'site_id',
    siteIdsTo: 'site_ids_to',
    virtualTransferItems: 'virtual.transfer_items',
    siteIdTo: 'id',
  };
  // only set value once for From site.
  public fillFromSiteByDefault: boolean = true;
  public transferSiteList$: BehaviorSubject<ISite[]> = new BehaviorSubject(
    null,
  );
  public siteModalDescription$: BehaviorSubject<string> =
    new BehaviorSubject<string>(
      this.i18nService.instant(I18n.Forms.GridMessages.ShowingEntities, {
        count: 0,
        entities: this.i18nService.instant(
          I18n.EntityNames.siteSingularAndPlural,
        ),
      }),
    );
  public virtualTransferItemsAction$: Subject<IWidgetAction> = new Subject();
  public sitesDisplayCount$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  public transferDestinationsDisplayCount$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  public transferDestinationsSelectedCount$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  public transferDestinationsDescription$: BehaviorSubject<string> =
    new BehaviorSubject<string>(
      this.i18nService.instant(
        I18n.Forms.GridMessages.ShowingEntitiesWithSelected,
        {
          count: 0,
          entities: this.i18nService.translate(
            I18n.EntityNames.SiteSingularAndPlural,
          ),
          selectedCount: 0,
        },
      ),
    );
  public listLoader$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public siteIdDataSource: DataSource;

  public constructor(public injector: Injector) {
    super(injector);

    this.initSiteIdDataSource();
  }

  public onFormReady(form): void {
    super.onFormReady(form);

    this.autoPopulate();

    this.getTransferSiteList();
    this.initDescriptionSubscriptions();
    this.initFromSiteSubscription();
    this.initDestinationsSubscription();
    this.handleFromSiteNotExist();

    this.siteIdDataSource?.refresh();
  }

  public destroyFormDefinition(): void {
    this.transferSiteList$.next(null);
    this.listLoader$.next(true);
    this.fillFromSiteByDefault = true;
    super.destroyFormDefinition();
  }

  public autoPopulate(): void {
    if (!this.isNewMode()) {
      return;
    }

    const autoPopulateData = getCreateAutoPopulateData(this.routeState$);

    if (isEmpty(autoPopulateData)) {
      return;
    }

    setFormFieldValue(
      this.form,
      this.formFieldPath.transferType,
      get(
        autoPopulateData,
        this.formFieldPath.transferType,
        this.getFormFieldValue(this.formFieldPath.transferType),
      ),
    );
  }

  public initSiteIdDataSource(): void {
    this.siteIdDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.SITE,
    );
    if (this.siteIdDataSource.service) {
      this.siteIdDataSource.service.quickFilterFields = [
        'name',
        'store_number',
      ];
    }
    this.siteIdDataSource.fetchList = this.siteIdDataSourceFetchList.bind(this);
  }

  public initDestinationsSubscription(): void {
    this.subscriptions.push(
      this.fieldValueChanges(this.formFieldPath.virtualTransferItems).subscribe(
        (virtualItems) => {
          const virtualItemIds = map(virtualItems, 'id');
          const siteFromId = getFormFieldValue(
            this.form,
            this.formFieldPath.siteId,
          );

          if (!isEmpty(siteFromId) && !includes(virtualItemIds, siteFromId)) {
            const siteFormFieldControl = this.getFormFieldControl(
              this.formFieldPath.siteId,
            );
            if (siteFormFieldControl) {
              siteFormFieldControl.setErrors(null);
            }
          }
        },
      ),
    );
  }

  public initDescriptionSubscriptions(): void {
    this.subscriptions.push(
      observableCombineLatest([
        this.transferDestinationsDisplayCount$,
        this.transferDestinationsSelectedCount$,
      ])
        .pipe(rx.distinctUntilChanged(isEqual))
        .subscribe(
          ([displayedCount, selectedCount]: [number, number]): void => {
            const generatedMsg =
              this.isEditMode() &&
              !this.permissionService.canManage(
                this.transferGroupTemplateService.config.ENTITY_TYPE,
              )
                ? I18n.Forms.GridMessages.ShowingEntities
                : I18n.Forms.GridMessages.ShowingEntitiesWithSelected;
            const sitesSubTitle = this.i18nService.instant(generatedMsg, {
              count: displayedCount,
              entities: this.i18nService.instant(
                I18n.EntityNames.SiteSingularAndPlural,
              ),
              selectedCount,
            });
            this.transferDestinationsDescription$.next(sitesSubTitle);
          },
        ),
    );
    this.subscriptions.push(
      this.sitesDisplayCount$.subscribe((count: number): void => {
        const sitesSubTitle = this.i18nService.instant(
          I18n.Forms.GridMessages.ShowingEntities,
          {
            count,
            entities: this.i18nService.instant(
              I18n.EntityNames.siteSingularAndPlural,
            ),
          },
        );
        this.siteModalDescription$.next(sitesSubTitle);
      }),
    );
  }

  public initFromSiteSubscription(): void {
    const subscription = observableCombineLatest([
      this.fieldValueChanges(this.formFieldPath.siteId).pipe(
        rx.distinctUntilChanged(),
      ),
      this.transferSiteList$.pipe(rx.distinctUntilChanged(isEqual)),
    ]).subscribe(([siteId, transferSites]: [string, ISite[]]): void => {
      if (this.isEditMode()) {
        return;
      }

      if (
        !isEmpty(transferSites) &&
        isEmpty(siteId) &&
        this.fillFromSiteByDefault
      ) {
        const autoPopulateData = getCreateAutoPopulateData(this.routeState$);

        let newSiteId = null;

        if (!isEmpty(autoPopulateData)) {
          this.fillFromSiteByDefault = false;
          const populateSiteId = get(
            autoPopulateData,
            this.formFieldPath.siteId,
          );

          const populateSite = find(transferSites, { id: populateSiteId });

          if (!isEmpty(populateSite)) {
            newSiteId = populateSiteId;
          }
        } else if (isSiteTypePrimaryWarehouse(head(transferSites))) {
          this.fillFromSiteByDefault = false;
          newSiteId = get(head(transferSites), 'id');
        }

        if (!isEmpty(newSiteId)) {
          setFormFieldValue(
            this.form,
            this.formFieldPath.siteId,
            newSiteId,
            false,
          );
        }
      }
    });
    this.subscriptions.push(subscription);
  }

  // handle from site is deleted in xop.
  public handleFromSiteNotExist(): void {
    this.subscriptions.push(
      // we should skip first value of transferSites, because when it init we get undefined.
      this.transferSiteList$.pipe(rx.skip(1)).subscribe((transferSites) => {
        const FromSiteNotExist = isNil(
          find(transferSites, (site) =>
            isEqual(
              getFormFieldValue(this.form, this.formFieldPath.siteId),
              site.id,
            ),
          ),
        );

        // when site is deleted in xop, should remove id received from backend.
        if (this.fillFromSiteByDefault && FromSiteNotExist) {
          setFormFieldValue(this.form, this.formFieldPath.siteId, null, false);
          this.fillFromSiteByDefault = false;
        }
      }),
    );
  }

  public siteIdDataSourceFetchList(
    query: IFilterQuery,
  ): Promise<IApiListResponse> {
    return this.fetchSiteList(query).then(
      (result) => result,
      (error) => {
        this.siteIdDataSource.error.next(error);

        return Promise.reject(error);
      },
    );
  }

  public async fetchSiteList(
    query: IFilterQuery,
    options?: { notJoinStoreNumber?: boolean },
  ): Promise<IApiListResponse> {
    if (!this.form) {
      return Promise.resolve({
        items: [],
      } as IApiListResponse<any>);
    }

    const queryWithDefaults: IFilterQuery = merge(
      {
        filter: `is_active eq true`,
        select: [
          'name',
          'id',
          'type',
          'business_type',
          'store_number',
          'metadata',
        ],
        include_inactive: false,
        filter_by_assigned_sites: false,
        ...query,
      },
      {
        top: MAX_PAGE_SIZE,
      },
    );

    return this.sitesService
      .fetchList(queryWithDefaults)
      .pipe(
        rx.first(),
        rx.map((response) => {
          let sites: ISite[] = get(response, 'items', []) as ISite[];
          sites = this.addPropertiesForDestination(sites);
          if (!get(options, 'notJoinStoreNumber', false)) {
            sites = addStoreNumberForSites(sites as ISite[]);
          }
          response.items = setPrimaryAndWarehouseTop(sites);

          return response;
        }),
      )
      .toPromise();
  }

  public async getTransferSiteList(): Promise<void> {
    const response = await this.fetchSiteList({}, { notJoinStoreNumber: true });
    this.initVirtualDestinations(response);
    this.transferSiteList$.next(response.items as ISite[]);
  }

  public initVirtualDestinations(
    response: IApiListResponse<IBaseEntityType>,
  ): void {
    if (this.isEditMode()) {
      const siteIdsTo = getFormFieldValue(
        this.form,
        this.formFieldPath.siteIdsTo,
      );
      const virtualDestinations = filter(
        map(siteIdsTo, (id) => {
          return find(response.items, { id });
        }),
        (site) => !isEmpty(site),
      );

      setFormFieldValue(
        this.form,
        this.formFieldPath.virtualTransferItems,
        virtualDestinations,
        false,
      );
    }

    this.listLoader$.next(false);
  }

  public async openSiteSelector(): Promise<void> {
    const siteIdValue = getFormFieldValue(this.form, this.formFieldPath.siteId);
    const transferSitesList$: Observable<ISite[]> = this.transferSiteList$.pipe(
      first(),
      rx.map((sites) => {
        const transferItems = getFormFieldValue(
          this.form,
          this.formFieldPath.virtualTransferItems,
        );

        return filterSelectedSites(
          [siteIdValue, ...map(transferItems, this.formFieldPath.siteIdTo)],
          sites,
        );
      }),
    );
    const transferSitesListPromise: ISite[] =
      await transferSitesList$.toPromise();
    const entityIdsForMetadataList: EntityId[] = chain(transferSitesListPromise)
      .flatMap('metadata')
      .map('entity_id')
      .value();

    const siteSelectorGridSettings: IModalDialogOptions = {
      title: I18n.Common.SiteSelector,
      subTitle: this.siteModalDescription$,
      modalClass: `modal-lg`,
      buttons: [
        {
          ...BTN_OK,
          caption: (selectedModalSites) =>
            getSiteSelectorButtonCaption({
              ...selectedModalSites,
              i18nService: this.i18nService,
            }),
          class: 'pull-right btn-primary',
        },
        BTN_CANCEL,
      ],
      schema: {
        properties: {
          items: { type: 'array', required: true },
        },
      },
      formRules: {
        fields: {
          items: {
            ignoreAutoTitleWhenTranslate: true,
            widgetData: {
              displayCountObs: this.sitesDisplayCount$,
              valueField: 'id',
              treeViewMode: true,
              treeChildrenPropertyName: 'children',
              treeColumnTitle: 'hierarchy',
              defaultExpanded: false,
              ignoreItemValueOptimization: true,
              useVisibleItemCountDescription: true,
              searchPlaceholder: I18n.Common.Search,
              columns: getSiteSelectorColumnsDefinition(
                entityIdsForMetadataList,
              ),
              userGridOptions: {
                overlayNoRowsTemplate: I18n.Common.NoRowsToShow,
              },
            },
          },
        },
      },
    };

    const virtualTransferItems = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    );
    const transferDestinationIds = map(virtualTransferItems, 'site_id_to');

    const modalOptions: IModalDialogOptions = merge(
      {},
      gridSelectModalOptions(transferSitesList$, this.i18nService),
      getSiteSelectorMaxLimitDefine(
        MAX_SITES_LIMIT,
        transferDestinationIds,
        this.i18nService.instant(I18n.InfoMessages.YouCanSelectUpToTargets, {
          maxCount: MAX_SITES_LIMIT,
          targetsOf: this.i18nService.instant(
            I18n.Forms.TransferForm.sitesAsTransferDestinations,
          ),
          alreadyAdded: `${size(transferDestinationIds)} ${this.i18nService.instant(I18n.EntityNames.siteSingularAndPlural)}`,
        }),
      ),
      defaultWidgetData,
      siteSelectorGridSettings,
    );

    const { confirmed, value } =
      await this.uiActions.formModalDialog(modalOptions);
    if (!confirmed) return;

    const primaryAndWarehouseTopSites: ISite[] = concat(
      value.items,
      virtualTransferItems,
    );
    const siteToIds = map(primaryAndWarehouseTopSites, 'id');
    setFormFieldValue(this.form, this.formFieldPath.siteIdsTo, siteToIds);

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
      primaryAndWarehouseTopSites,
    );
  }

  public addPropertiesForDestination(
    items: ISite[],
  ): ITransferGroupTemplateSite[] {
    return map(items, (item) => ({
      ...item,
      site_name_to: get(item, 'name'),
    }));
  }

  public composeConfirmationModalOptions(action: string): IModalDialogOptions {
    if (action === BUTTON_ACTIONS.REMOVE) {
      return {
        text: I18n.InfoMessages.ConfirmationModalRemovesSites,
      };
    }
  }

  public getFromSiteRepeatValidator = (): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlValue = control.value;
      if (!this.form) return;
      const virtualItems = getFormFieldValue(
        this.form,
        this.formFieldPath.virtualTransferItems,
      );
      const siteToIds = map(virtualItems, 'id');
      const isRepeatWithSitesTo = includes(siteToIds, controlValue);

      return isRepeatWithSitesTo
        ? {
            error: this.i18nService.instant(
              I18n.ErrorMessages.TransferSiteSameError,
            ),
          }
        : null;
    };
  };
}
